import { userRoles } from '@r/types/index.ts';

export type Role = (typeof userRoles)[number];

export function isValidRole(value: string): value is Role {
  return userRoles.includes(value as Role);
}

/**
 * Retourne si l'utilisateur est administrateur.
 */
export function isAdminRole(role: Role | undefined): boolean {
  return role?.includes('_ADMIN') ?? false;
}

/**
 * Retourne si l'utilisateur a au minimum le rôle ROLE_ADMIN.
 */
export function isAdminOrHigher(role: Role | undefined): boolean {
  return role === 'ROLE_ADMIN' || role === 'ROLE_FIDDY_ADMIN';
}

/**
 * Retourne les rôles assignables. Seuls les rôles admin peuvent assigner.
 */
export function getAssignableRoles(role: Role | undefined): Role[] {
  return getRolesInternal(role, 'assign');
}

/**
 * Retourne les rôles filtrables. Un utilisateur peut filtrer à partir de son rôle jusqu'au plus bas.
 */
export function getFilterableRoles(role: Role | undefined): Role[] {
  return getRolesInternal(role, 'filter');
}

/**
 * Convertit un rôle en clé i18n (ROLE_MEMBER → role.member).
 */
export function roleToI18nKey(role: Role): string {
  return role.replace('ROLE_', 'role.').toLowerCase().replace(/_/g, '.');
}

/**
 * Indique si le filtre commercial doit être visible.
 */
export function canUseSalesFilter(roles?: string[]): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_FIDDY_ADMIN');
}

type RoleContext = 'assign' | 'filter';

const BASE_ROLES = ['ROLE_MEMBER', 'ROLE_MANAGER'] as const;
const SALES_ADMIN_ROLES = ['ROLE_MEMBER', 'ROLE_MANAGER', 'ROLE_SALES_ADMIN'] as const;

function getRolesInternal(role: Role | undefined, context: RoleContext): Role[] {
  const allRoles = userRoles as readonly Role[];

  if (!role) {
    return context === 'assign' ? [] : [...BASE_ROLES];
  }

  switch (role) {
    case 'ROLE_FIDDY_ADMIN':
      return [...allRoles];

    case 'ROLE_ADMIN':
      return allRoles.filter((r) => r !== 'ROLE_FIDDY_ADMIN');

    case 'ROLE_SALES_ADMIN':
      return context === 'assign' ? [...BASE_ROLES] : [...SALES_ADMIN_ROLES];

    default:
      return context === 'assign' ? [] : [...BASE_ROLES];
  }
}
